import { Check, Lock, AlertCircle } from "lucide-react";
import { cn } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { validateStageProgression, type StageValidationResult } from "@/utils/stageValidation";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

interface WorkflowBarProps {
  currentStatus: string;
  jobType: string;
  jobId: string;
  onStatusChange: (status: string) => void;
}

interface WorkflowStage {
  id: string;
  stage_key: string;
  stage_name: string;
  stage_order: number;
  color: string;
}

export const WorkflowBar = ({ currentStatus, jobType, jobId, onStatusChange }: WorkflowBarProps) => {
  const [stages, setStages] = useState<WorkflowStage[]>([]);
  const [loading, setLoading] = useState(true);
  const [showRequirementsDialog, setShowRequirementsDialog] = useState(false);
  const [validationResult, setValidationResult] = useState<StageValidationResult | null>(null);
  const [targetStageName, setTargetStageName] = useState<string>('');

  useEffect(() => {
    loadWorkflowStages();
  }, [jobType]);

  // Normalize job type to workflow type (case-insensitive, handles variations)
  const normalizeType = (type: string): 'retail' | 'insurance' | 'commercial' => {
    const normalized = type?.toLowerCase().trim();
    if (normalized === 'retail' || normalized === 'residential') return 'retail';
    if (normalized === 'insurance') return 'insurance';
    return 'commercial';
  };

  const loadWorkflowStages = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data: profile } = await supabase
        .from("profiles")
        .select("org_id")
        .eq("id", user.id)
        .single();

      if (!profile?.org_id) return;

      // Map job type to workflow type with normalization
      const workflowType = normalizeType(jobType);

      // Load workflow and stages
      const { data: workflow } = await supabase
        .from('workflows')
        .select(`
          id,
          workflow_stages (*)
        `)
        .eq('org_id', profile.org_id)
        .eq('workflow_type', workflowType)
        .maybeSingle();

      if (workflow?.workflow_stages) {
        const sortedStages = (workflow.workflow_stages as any[]).sort((a, b) => 
          a.stage_order - b.stage_order
        );
        setStages(sortedStages);
      } else {
        // Use default stages from useWorkflows.ts defaults
        const defaultStages = getDefaultStagesForType(workflowType);
        setStages(defaultStages);
      }
    } catch (error) {
      console.error('Error loading workflow stages:', error);
    } finally {
      setLoading(false);
    }
  };

  // Default stages matching useWorkflows.ts structure
  const getDefaultStagesForType = (workflowType: 'retail' | 'insurance' | 'commercial'): WorkflowStage[] => {
    const defaults = {
      retail: [
        { id: '1', stage_key: 'new', stage_name: 'Lead', stage_order: 1, color: '#3b82f6' },
        { id: '2', stage_key: 'contacted', stage_name: 'Contacted', stage_order: 2, color: '#a855f7' },
        { id: '3', stage_key: 'scheduled', stage_name: 'Appointment Set', stage_order: 3, color: '#f97316' },
        { id: '4', stage_key: 'in_progress', stage_name: 'In Progress', stage_order: 4, color: '#06b6d4' },
        { id: '5', stage_key: 'proposed', stage_name: 'Proposal Sent', stage_order: 5, color: '#22c55e' },
        { id: '6', stage_key: 'completed', stage_name: 'Closed Won', stage_order: 6, color: '#10b981' },
      ],
      insurance: [
        { id: '1', stage_key: 'new', stage_name: 'Lead', stage_order: 1, color: '#3b82f6' },
        { id: '2', stage_key: 'contacted', stage_name: 'Inspection/Claim Filed', stage_order: 2, color: '#a855f7' },
        { id: '3', stage_key: 'scheduled', stage_name: 'Adjuster Meeting', stage_order: 3, color: '#f97316' },
        { id: '4', stage_key: 'approved', stage_name: 'Claim Approved', stage_order: 4, color: '#8b5cf6' },
        { id: '5', stage_key: 'inspected', stage_name: 'Proposal Sent', stage_order: 5, color: '#06b6d4' },
        { id: '6', stage_key: 'in_progress', stage_name: 'In Progress', stage_order: 6, color: '#22c55e' },
        { id: '7', stage_key: 'completed', stage_name: 'Closed Won', stage_order: 7, color: '#10b981' },
      ],
      commercial: [
        { id: '1', stage_key: 'new', stage_name: 'Lead', stage_order: 1, color: '#3b82f6' },
        { id: '2', stage_key: 'contacted', stage_name: 'Site Visit/Inspection', stage_order: 2, color: '#a855f7' },
        { id: '3', stage_key: 'scheduled', stage_name: 'Bidding/Tender', stage_order: 3, color: '#f59e0b' },
        { id: '4', stage_key: 'inspected', stage_name: 'Proposal Submitted', stage_order: 4, color: '#06b6d4' },
        { id: '5', stage_key: 'approved', stage_name: 'Contract Signed', stage_order: 5, color: '#8b5cf6' },
        { id: '6', stage_key: 'in_progress', stage_name: 'In Progress', stage_order: 6, color: '#22c55e' },
        { id: '7', stage_key: 'completed', stage_name: 'Closed Won', stage_order: 7, color: '#10b981' },
      ],
    };
    return defaults[workflowType];
  };

  // Find current stage index based on status
  const getCurrentStageIndex = () => {
    const index = stages.findIndex(s => s.stage_key === currentStatus);
    return index >= 0 ? index : 0;
  };

  const currentStageIndex = getCurrentStageIndex();

  const handleStageClick = async (stage: WorkflowStage, index: number) => {
    // Can always go backwards
    if (index <= currentStageIndex) {
      onStatusChange(stage.stage_key);
      return;
    }

    // Can only progress to next stage
    if (index > currentStageIndex + 1) {
      toast.error('Complete current stage before skipping ahead');
      return;
    }

    // Validate requirements for target stage
    const validation = await validateStageProgression(jobId, stage.stage_key);
    
    if (validation.canProgress) {
      onStatusChange(stage.stage_key);
      toast.success(`Moved to ${stage.stage_name}`);
    } else {
      setValidationResult(validation);
      setTargetStageName(stage.stage_name);
      setShowRequirementsDialog(true);
    }
  };

  if (loading || stages.length === 0) {
    return (
      <div className="border-b-2 border-primary/20 bg-gradient-to-b from-background via-primary/5 to-background shadow-md">
        <div className="container mx-auto px-4 sm:px-6 py-6 sm:py-8">
          <div className="text-center text-muted-foreground">Loading workflow...</div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="border-b-2 border-primary/20 bg-gradient-to-b from-background via-primary/5 to-background shadow-md">
        <div className="container mx-auto px-4 sm:px-6 py-6 sm:py-8">
          <div className="flex flex-col items-center mb-6 sm:mb-8">
            <h3 className="text-sm sm:text-base font-black uppercase tracking-widest text-primary mb-2">
              Job Stage
            </h3>
            <Badge variant="outline" className="text-xs font-semibold px-3 py-1">
              {stages[currentStageIndex]?.stage_name || "Unknown"}
            </Badge>
          </div>
          <div className="overflow-x-auto pb-2">
            <div className="flex items-center justify-center gap-3 sm:gap-6 min-w-max mx-auto">
              {stages.map((stage, index) => {
                const isCompleted = index < currentStageIndex;
                const isCurrent = index === currentStageIndex;
                const isNext = index === currentStageIndex + 1;
                const isBlocked = index > currentStageIndex + 1;
                const stageColor = stage.color;

                return (
                  <div key={stage.id} className="flex items-center flex-shrink-0">
                    <div className="flex flex-col items-center gap-3 sm:gap-4">
                      <button
                        onClick={() => handleStageClick(stage, index)}
                        className={cn(
                          "relative flex items-center justify-center w-14 h-14 sm:w-16 sm:h-16 rounded-full text-base sm:text-lg font-black transition-all duration-300 shadow-2xl",
                          isCompleted || isCurrent
                            ? "text-white cursor-pointer hover:scale-110"
                            : isNext
                            ? "bg-muted/80 text-muted-foreground cursor-pointer hover:scale-110 hover:bg-muted"
                            : isBlocked
                            ? "bg-muted/50 text-muted-foreground/50 cursor-not-allowed"
                            : "bg-muted/80 text-muted-foreground cursor-pointer hover:scale-110 hover:bg-muted"
                        )}
                        style={
                          isCompleted || isCurrent
                            ? { 
                                backgroundColor: stageColor,
                                boxShadow: `0 0 30px ${stageColor}40`,
                              }
                            : undefined
                        }
                        disabled={isBlocked}
                      >
                        {isCompleted ? (
                          <Check className="w-6 h-6 sm:w-7 sm:h-7" />
                        ) : isBlocked ? (
                          <Lock className="w-5 h-5 sm:w-6 sm:h-6" />
                        ) : (
                          index + 1
                        )}
                      </button>
                      <span
                        className={cn(
                          "text-xs sm:text-sm font-bold whitespace-nowrap text-center",
                          isCompleted || isCurrent
                            ? "text-foreground"
                            : "text-muted-foreground"
                        )}
                      >
                        {stage.stage_name}
                      </span>
                    </div>
                    {index < stages.length - 1 && (
                      <div className="w-12 sm:w-16 h-2 sm:h-2.5 mx-3 sm:mx-4 bg-muted/50 rounded-full overflow-hidden shadow-inner flex-shrink-0">
                        <div
                          className={cn(
                            "h-full transition-all duration-500",
                            isCompleted ? "w-full" : "w-0"
                          )}
                          style={
                            isCompleted
                              ? {
                                  background: `linear-gradient(to right, ${stageColor}, ${stageColor}cc)`,
                                  boxShadow: `0 0 15px ${stageColor}60`
                                }
                              : undefined
                          }
                        />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      <Dialog open={showRequirementsDialog} onOpenChange={setShowRequirementsDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-destructive" />
              Requirements Not Met
            </DialogTitle>
            <DialogDescription>
              Complete the following requirements before moving to <strong>{targetStageName}</strong>:
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-4">
            {validationResult?.completedRequirements && validationResult.completedRequirements.length > 0 && (
              <div className="space-y-2">
                <h4 className="text-sm font-semibold text-green-600 dark:text-green-400">
                  ✓ Completed
                </h4>
                {validationResult.completedRequirements.map((req, i) => (
                  <div key={i} className="text-sm text-muted-foreground pl-4">
                    • {req}
                  </div>
                ))}
              </div>
            )}
            {validationResult?.missingRequirements && validationResult.missingRequirements.length > 0 && (
              <div className="space-y-2">
                <h4 className="text-sm font-semibold text-destructive">
                  ✗ Missing Requirements
                </h4>
                {validationResult.missingRequirements.map((req, i) => (
                  <div key={i} className="text-sm pl-4 flex items-start gap-2">
                    <span className="text-destructive mt-0.5">•</span>
                    <span>{req}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
